import { ISODateStringToTimestamp } from '@sky-it-livedata-libraries/livedata-commons';

import { IStandingRows } from '../../../types/standing';
import { IDriversSessionStandingRow } from '../../../types/drivers-ranking';
import { IGrandPrixSessions } from '../../../types/grand-prix-sessions';
import { IDriverSessionDetail, ISession, ISessionInfo } from '../../../types/session';

import {  compileSessionRowsForSessionStatus,  getStandingVariantByRaceSession, showTimingsForSessionStatus } from '../../../utils/race-session';
import { getTeamNameDriverSessionDetail } from '../../../utils/team';
import { getDriverName } from '../../../utils/driver';
import { WidgetError, ErrorName } from '../../../utils/errors';

const DRIVER_DETAIL_FALLBACK_LABEL = '-';

export const isValidDriverSessionDetail = (driver: IDriverSessionDetail) =>
    driver && driver.firstName && driver.lastName;

/**
 * Transform each row
 * @param row data passed to transformer
 */
export const transformRow = (row: IDriverSessionDetail): IDriversSessionStandingRow => {
    if (isValidDriverSessionDetail(row)) {
      const {
        customDriverPageUrl: driverUrl,
        customTeamPageUrl: teamUrl,
        fastestLap = DRIVER_DETAIL_FALLBACK_LABEL,
        flagUrl: logoUrl,
        gap = DRIVER_DETAIL_FALLBACK_LABEL,
        isFastestLap = false,
        points,
        position = DRIVER_DETAIL_FALLBACK_LABEL,
        timing,
      } = row;
  
      return {
        driverName: getDriverName(row),
        driverUrl,
        fastestLap,
        gap,
        isFastestLap,
        logoUrl,
        points: points?.personPoints || DRIVER_DETAIL_FALLBACK_LABEL,
        position,
        teamName: getTeamNameDriverSessionDetail(row),
        teamUrl,
        timing,
      };
    } else {
        throw new WidgetError<ErrorName>({
            name: 'NO_VALID_DATA',
            message: 'Invalid transformer input, Error in SessionTransformer object',
            data: row
        });
    }
  };

export const transformSession = (
    session: ISession,
    trackingData: object,
    gpName?: string,
    gpPageUrl?: string,
    gpCrossLink?: string
  ): IStandingRows<IDriversSessionStandingRow> => {
    try {
      const sessionInfo: ISessionInfo = session.sessionInfo;
      const { sessionStatus: status, sessionLabel: tableTitle, startDateTime, details } = sessionInfo;
     
      const rows: readonly IDriversSessionStandingRow[] =
        details && compileSessionRowsForSessionStatus(status)
          ? details.map((detail: IDriverSessionDetail) => transformRow(detail))
          : [];

      const standingRows: IStandingRows<IDriversSessionStandingRow> = {
        rankingVariant: getStandingVariantByRaceSession(tableTitle),
        rows,
        showTimings: showTimingsForSessionStatus(status),
        startDateTime,
        status,
        tableTitle,
        trackingData,
      };
  
      
      return {
        ...(gpName && { gpName }),
        ...(gpPageUrl && { gpPageUrl }),
        ...(gpCrossLink && { lgpLink: gpCrossLink }),
        ...standingRows,
      };
    } catch (error) {
        throw new WidgetError<ErrorName>({
            name: 'NO_VALID_DATA',
            message: 'Invalid transformer input, Error in SessionTransformer object',
        });
    }
  };


export const transformGrandPrixSessions = (
    sessions: ReadonlyArray<ISession> = [],
    trackingData: object,
    gpName?: string,
    gpPageUrl?: string,
    gpCrossLink?: string
  ): IGrandPrixSessions =>
    sessions
      .map((session: ISession) => transformSession(session, trackingData, gpName, gpPageUrl, gpCrossLink))
      .sort(
        (prev: IStandingRows<IDriversSessionStandingRow>, next: IStandingRows<IDriversSessionStandingRow>) =>
          (ISODateStringToTimestamp(next.startDateTime) ?? 0) - (ISODateStringToTimestamp(prev.startDateTime) ?? 0)
      );